import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { PriceLabel } from '../../components/PriceLabel'
import { Price } from '../../components/Price'
import { Box } from '../../components/Box'
import { Button } from '../../components/Button'
import { Flex } from '../../components/Flex'
import { Icon } from '../../components/Icons'
import { Image } from '../../components/Image'
import { LabeledValue } from '../../components/LabeledValue'
import { Text } from '../../components/Text'
import { ErrorText } from '../../components/Text/ErrorText'
import { themeColor } from '../../components/theme'
import { ReportUmbracoData } from '../../containers/CompanyDetail/Reports'
import { PriceVisibility } from '../../containers/PriceVisibility'
import { useDateFormat } from '../../hooks/useDateFormat'
import { getPricesMode, getPrimaryIdentifier, PrimaryIdentifier } from '../../redux/config'
import { Domain, DomainType } from '../../redux/domains'
import {
  isLoadingOrderItem,
  isRemovingOrderItem,
  OrderActions,
  OrderItem,
  OrderItemStatusCode
} from '../../redux/order'
import { getContractType } from '../../redux/user/selectors'
import { createTFunction, getDomainDescription } from '../../utils/helpers'
import { useViewportSize } from '../../hooks'
import { CompanyLink } from '../../components/CompanyLink/CompanyLink'

import { CartItemError } from './CartItemError'

const documentIcon = <Image alt="document" type="fileText" />

const ReportName = styled(Text)`
  color: ${themeColor('primary')};
  font-size: 20px;
  padding-left: 12px;
  white-space: nowrap;
`

const CartItemWrapper = styled(Flex)`
  background: ${themeColor('bgPrimary')};
  border-radius: 4px;
`

type CartItemProps = {
  data: OrderItem
  domainsData: Partial<Record<DomainType, Domain>>
  umbracoData?: ReportUmbracoData
}

type CartItemBaseProps = {
  children: React.ReactNode | React.ReactNode[]
  isMobile?: boolean
} & CartItemProps

const CartItemBase = ({ children, data, domainsData, umbracoData, isMobile }: CartItemBaseProps) => {
  const { t } = useTranslation(['form', 'cartItem'])
  const { formatDate } = useDateFormat()

  const deliveryOption = getDomainDescription(DomainType.DeliveryOption, data.deliveryOptionCode, domainsData)
  const primaryIdentifier = useSelector(getPrimaryIdentifier)
  const viewPort = useViewportSize()

  return (
    <CartItemWrapper width={1} mb="20px" flexDirection="column" data-automation-id="cartItemWrapper">
      {data.companyIdentifier && (
        <Flex width={1} pt={1} px={1} justifyContent={viewPort?.isMobile ? 'end' : 'start'}>
          <CompanyLink
            companyIdentifier={data.countryCode + '-' + data.companyIdentifier}
            companyName={data.companyName}
            companyIdentifierType={data.identifierType}
          >
            <Image type="arrowLeftBlue" />
          </CompanyLink>
        </Flex>
      )}
      <Flex flexWrap="wrap" pt={data.companyIdentifier ? 1 : 3}>
        {primaryIdentifier === PrimaryIdentifier.Report && (
          <LabeledValue
            px={3}
            width={[1, 1, 5 / 12]}
            disableMarginBottom
            label={
              data.companyIdentifier && data.countryCode ? (
                <CompanyLink
                  companyIdentifier={data.countryCode + '-' + data.companyIdentifier}
                  companyName={data.companyName}
                  companyIdentifierType={data.identifierType}
                >
                  <Flex>
                    {documentIcon}
                    <ReportName data-automation-id="cartItemReportName">{umbracoData?.name}</ReportName>
                  </Flex>
                </CompanyLink>
              ) : (
                <Flex>
                  {documentIcon}
                  <ReportName data-automation-id="cartItemReportName">{umbracoData?.name}</ReportName>
                </Flex>
              )
            }
            value={
              <>
                {!isMobile && (
                  <span
                    style={{ display: 'flex' }}
                    dangerouslySetInnerHTML={{ __html: umbracoData?.previewDescription || '' }}
                  />
                )}
              </>
            }
          />
        )}

        <LabeledValue
          px={3}
          pb={[0, 0, 2]}
          width={[1, 1, primaryIdentifier === PrimaryIdentifier.Report ? 2 / 12 : 4 / 12]}
          pt={2}
          disableMarginBottom
          label={
            data.companyIdentifier && data.countryCode ? (
              <CompanyLink
                companyIdentifier={data.countryCode + '-' + data.companyIdentifier}
                companyName={data.companyName}
                companyIdentifierType={data.identifierType}
              >
                <Text color="primary">{t('form:Company')}</Text>
              </CompanyLink>
            ) : (
              <Text color="primary">{t('form:Company')}</Text>
            )
          }
          value={data.companyName}
        />

        {primaryIdentifier === PrimaryIdentifier.Company && (
          <LabeledValue
            px={3}
            pb={[0, 0, 2]}
            width={[1, 1, 3 / 12]}
            pt={2}
            disableMarginBottom
            label={
              data.companyIdentifier && data.countryCode ? (
                <CompanyLink
                  companyIdentifier={data.countryCode + '-' + data.companyIdentifier}
                  companyName={data.companyName}
                  companyIdentifierType={data.identifierType}
                >
                  <Text color="primary">{t('form:ReportName')}</Text>
                </CompanyLink>
              ) : (
                <Text color="primary">{t('form:ReportName')}</Text>
              )
            }
            value={umbracoData?.name}
          />
        )}

        <Box width={[1, 1, 2 / 12]} px={3} pb={[0, 0, 2]} pt={2}>
          {data.documentDate ? (
            <LabeledValue
              width={1}
              disableMarginBottom
              label={t('form:DocumentDate')}
              value={formatDate(new Date(data.documentDate), { dateStyle: 'medium' })}
            />
          ) : (
            <>
              <LabeledValue
                width={1}
                disableMarginBottom
                label={t('form:Language')}
                value={getDomainDescription(DomainType.Culture, data.languageCode, domainsData)}
              />
              {deliveryOption && (
                <LabeledValue
                  width={1}
                  pt={[2, 2, 3]}
                  disableMarginBottom
                  label={t('form:DeliveryOption')}
                  value={getDomainDescription(DomainType.DeliveryOption, data.deliveryOptionCode, domainsData)}
                />
              )}

              {data.lastUpdateDate && (
                <LabeledValue
                  width={1}
                  pt={[2, 2, 3]}
                  disableMarginBottom
                  label={t('form:LastUpdate')}
                  value={formatDate(new Date(data.lastUpdateDate), { dateStyle: 'medium' })}
                />
              )}
            </>
          )}
        </Box>
        {children}
      </Flex>
    </CartItemWrapper>
  )
}

export const CartItemComponent = ({ data, domainsData, umbracoData }: CartItemProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['form', 'core'])
  const tc = createTFunction('core:', t)

  const isRemoving = useSelector((state: any) => isRemovingOrderItem(state))

  const isLoading = useSelector((state: any) =>
    isLoadingOrderItem(state, { customerRequestId: data.customerRequestId })
  )

  const contractType = useSelector(getContractType)
  const pricesMode = useSelector(getPricesMode)
  const errorMessage = CartItemError(data)

  return (
    <CartItemBase data={data} domainsData={domainsData} umbracoData={umbracoData}>
      {data.statusCode !== OrderItemStatusCode.Error && (
        <PriceVisibility>
          <LabeledValue
            px={3}
            pb={3}
            width={[1, 1, 2 / 12]}
            pt={2}
            disableMarginBottom
            label={PriceLabel({ pricesMode, label: tc('Price'), netLabel: tc('PriceSSTExcluded') })}
            value={
              <Text color="secondary" fontSize="24px" bold data-automation-id="cartItemPrice">
                {Price({
                  pricesMode,
                  contractType,
                  grossValue: data.valueWithTaxFormatted,
                  netValue: data.netValueFormatted
                })}
              </Text>
            }
          />
        </PriceVisibility>
      )}
      {data.statusCode === OrderItemStatusCode.Error && (
        <Box px={3} pb={3} width={[1, 1, 2 / 12]} pt={2}>
          <ErrorText border>{errorMessage}</ErrorText>
        </Box>
      )}
      <Flex px={3} pb={3} width={[1, 1, 1 / 12]} alignItems="center" justifyContent="flex-end" flex="1 1 auto">
        <Button
          variant="primary"
          padding="5px 12px"
          onClick={() => dispatch(OrderActions.removeOrderItem({ customerRequestId: data.customerRequestId }))}
          disabled={isRemoving}
          loading={isLoading}
          data-automation-id="cartItemTrash"
        >
          <Icon type="Trash" />
        </Button>
      </Flex>
    </CartItemBase>
  )
}

export const CartItemMobileComponent = ({ data, domainsData, umbracoData }: CartItemProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['form', 'core', 'cartItem'])
  const tc = createTFunction('core:', t)
  const isRemoving = useSelector((state: any) => isRemovingOrderItem(state))

  const isLoading = useSelector((state: any) =>
    isLoadingOrderItem(state, { customerRequestId: data.customerRequestId })
  )

  const contractType = useSelector(getContractType)
  const pricesMode = useSelector(getPricesMode)

  return (
    <CartItemBase data={data} domainsData={domainsData} umbracoData={umbracoData} isMobile>
      <Flex flex="1 1 auto" justifyContent="space-between" alignItems="center" px={3} pb={3} pt={2}>
        <Button
          variant="primary"
          padding="5px 12px"
          onClick={() => dispatch(OrderActions.removeOrderItem({ customerRequestId: data.customerRequestId }))}
          loading={isRemoving}
          disabled={isLoading}
        >
          <Icon type="Trash" />
        </Button>
        {data.statusCode !== OrderItemStatusCode.Error && (
          <PriceVisibility>
            <LabeledValue
              px={3}
              pb={3}
              pt={2}
              disableMarginBottom
              label={PriceLabel({ pricesMode, label: tc('Price'), netLabel: tc('PriceSSTExcluded') })}
              value={
                <Text color="secondary" fontSize="24px" bold>
                  {Price({
                    pricesMode,
                    contractType,
                    grossValue: data.valueWithTaxFormatted,
                    netValue: data.netValueFormatted
                  })}
                </Text>
              }
            />
          </PriceVisibility>
        )}
        {data.statusCode === OrderItemStatusCode.Error && (
          <Box px={3} pb={3} pt={2}>
            <ErrorText>{t('cartItem:Error')}</ErrorText>
          </Box>
        )}
      </Flex>
    </CartItemBase>
  )
}
